import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';
import { SelectFieldOption } from '../select-field-option';
import { getUuidV4 } from '../../utils/uuid.utils';

/**
 * Low-level radio group for signal forms.
 *
 * A radio group is single-select by definition, so its value is the selected option's
 * value — a plain `string`, empty when nothing is chosen — rather than the `string[]` a
 * dropdown carries. That means it needs no new base: `SignalStringFieldBaseDirective`
 * already provides the model and the `onInput` handler, and a radio's `change` event
 * reports the chosen input's `value` exactly as a text input reports its text.
 *
 * ```html
 * <pace-core-radio-field [formField]="signalForm.workCode" [options]="workCodes()" />
 * ```
 *
 * Options come in as {@link SelectFieldOption}, the same shape the dropdown takes, since
 * both are usually fed from the same value help.
 *
 * `required` is meaningful here, unlike on a checkbox or a dropdown: the empty string is
 * unambiguously "nothing chosen", so the schema's own `required` rule applies.
 *
 * Not carried over from the reactive-forms `RadioInputComponent`: `initialSelection`
 * (the container seeds the slice instead) and `invalidSelection`, which marks one option
 * as invalid and therefore belongs in the schema as a rule on the value.
 */
@Component({
    selector: 'pace-radio-field',
    templateUrl: './radio-field.component.html',
    styleUrls: ['./radio-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioFieldComponent extends SignalStringFieldBaseDirective {
    public readonly options = input<readonly SelectFieldOption[]>([]);

    /** stacks the options on narrow screens and lays them out in a row from md up */
    public readonly useResponsiveLayout = input(false);

    /**
     * Name shared by the radio inputs, which is what makes them one group. Generated per
     * instance so two groups on the same screen cannot capture each other's clicks.
     */
    public readonly groupName = input(`radio-field-${getUuidV4()}`);

    public isSelected(option: SelectFieldOption): boolean {
        return this.value() === option.value;
    }

    /**
     * Choosing an option is an interaction, but clicking a radio produces no blur — so
     * unlike the text fields the group has to report `touched` here rather than relying on
     * the base's blur handling.
     *
     * @param event the native change event, whose target carries the chosen option's value
     */
    public onChange(event: Event): void {
        this.value.set((<HTMLInputElement>event.target).value);
        this.touched.set(true);
    }

    /**
     * @param option the option to build an id for
     * @returns a per-group, per-option id tying the label to its input
     */
    public optionId(option: SelectFieldOption): string {
        return `${this.groupName()}-${option.value}`;
    }
}
